/**
 * General component for creating alert dialogs
 *
 * @prop {bool} visible - Set whether dialog is shown/open
 * @prop {string} title - Optional dialog title
 * @prop {string} alertMessage - Message for dialog main body
 * @prop {string} cancelButtonText - cancel button text
 * @prop {string} acceptButtonText - accept button text
 * @prop {object} cancelButtonStyle - cancel button style ({ style: flat|any, primary: true|false })
 * @prop {object} acceptButtonStyle - accept button style ({ style: flat|any, primary: true|false })
 * @prop {function} onCancel - function executed when cancel button is clicked
 * @prop {function} onAccept - function executed when accept button is clicked
 * @prop {function} formatMessage - locale formatting function
 *
 * @returns Alert dialog
 */

import React from 'react';
import PropTypes from 'prop-types';

import Dialog from 'material-ui/Dialog';
import FlatButton from 'material-ui/FlatButton';
import RaisedButton from 'material-ui/RaisedButton';

const AlertDialog = ({
  visible,
  title,
  alertMessage,
  cancelButtonText,
  acceptButtonText,
  cancelButtonStyle,
  acceptButtonStyle,
  onCancel,
  onAccept,
  formatMessage,
  dontFormatMessage,
  autoScrollBodyContent = false
}) => {
  const acceptButton = onAccept ? (
    acceptButtonStyle.button === 'flat' ? (
      <FlatButton
        label={formatMessage({ id: acceptButtonText })}
        onClick={onAccept}
        style={{ float: 'left' }}
        primary={!!acceptButtonStyle.primary}
      />
    ) : (
      <RaisedButton
        label={formatMessage({ id: acceptButtonText })}
        onClick={onAccept}
        style={{ float: 'left' }}
        primary={!!acceptButtonStyle.primary}
      />
    )
  ) : (
    <div
      style={{
        /** This to align accept button properly */
        clear: 'both',
      }}
    />
  );
  return (
    <Dialog
      title={title ? formatMessage({ id: title }) : undefined}
      open={visible}
      autoScrollBodyContent={autoScrollBodyContent}
      actions={[
        acceptButton,
        cancelButtonStyle.button === 'flat' ? (
          <FlatButton
            label={formatMessage({ id: cancelButtonText })}
            onClick={onCancel}
            primary={!!cancelButtonStyle.primary}
          />
        ) : (
          <RaisedButton
            label={formatMessage({ id: cancelButtonText })}
            onClick={onCancel}
            primary={!!cancelButtonStyle.primary}
          />
        ),
      ]}
    >
      {dontFormatMessage ? alertMessage : formatMessage({ id: alertMessage })}
    </Dialog>
  );
};

AlertDialog.propTypes = {
  visible: PropTypes.bool,
  title: PropTypes.string,
  alertMessage: PropTypes.string,
  cancelButtonText: PropTypes.string,
  acceptButtonText: PropTypes.string,
  cancelButtonStyle: PropTypes.object,
  acceptButtonStyle: PropTypes.object,
  onCancel: PropTypes.func,
  onAccept: PropTypes.func,
  formatMessage: PropTypes.func,
  dontFormatMessage: PropTypes.bool,
  autoScrollBodyContent: PropTypes.bool
};

export default AlertDialog;
